import { Box, Typography, TextField, Slider, useTheme, Paper, FormControl, InputLabel, Select, MenuItem, Button, Chip, Stack } from '@mui/material';
import { useLLMSettings } from '../../contexts/LLMSettingsContext.jsx';
import { useState, useEffect, useContext } from 'react';
import { UserContext } from '../Auth.jsx';

// Function to estimate token count from text
const estimateTokens = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+|(?=\W)|(?<=\W)/).filter(Boolean).length;
};

export default function ChatSettings() {
  const theme = useTheme();
  const { user } = useContext(UserContext);
  const {
    systemPrompt,
    updateSystemPrompt,
    userPrompt,
    updateUserPrompt,
    historyLimit,
    updateHistoryLimit,
    universalPrompts,
    loadedUniversalPromptId,
    universalPromptsLoading,
    loadUniversalPrompt,
    clearUniversalPrompt,
    resetToUniversalPrompt,
  } = useLLMSettings();

  const [systemTokenCount, setSystemTokenCount] = useState(0);
  const [userTokenCount, setUserTokenCount] = useState(0);

  // Update token counts when prompts change
  useEffect(() => {
    setSystemTokenCount(estimateTokens(systemPrompt));
    setUserTokenCount(estimateTokens(userPrompt));
  }, [systemPrompt, userPrompt]);

  return (
    <Paper elevation={2} sx={{ padding: 3, marginBottom: 3 }}>
      <Typography variant="h6" sx={{ marginBottom: 3, fontWeight: 'medium' }}>
        Chat Settings
      </Typography>
      
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 'medium' }}>
          System Prompt:
        </Typography>
        
        {user?.admin ? (
          <>
            {/* Admin users can manage universal prompts and edit system prompt */}
            {/* Universal Prompt Selector */}
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth sx={{ mb: 1 }}>
                <InputLabel size="small">Load Universal Prompt (Optional)</InputLabel>
                <Select
                  size="small"
                  value={loadedUniversalPromptId || ''}
                  onChange={(e) => {
                    if (e.target.value) {
                      loadUniversalPrompt(parseInt(e.target.value));
                    }
                  }}
                  label="Load Universal Prompt (Optional)"
                  disabled={universalPromptsLoading}
                >
                  <MenuItem value="">
                    <em>None - Use custom system prompt</em>
                  </MenuItem>
                  {universalPrompts.map((prompt) => (
                    <MenuItem key={prompt.id} value={prompt.id}>
                      {prompt.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {/* Universal Prompt Actions */}
              {loadedUniversalPromptId && (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  <Chip 
                    label={`Loaded: ${universalPrompts.find(p => p.id === parseInt(loadedUniversalPromptId))?.title || 'Unknown'}`}
                    color="success"
                    variant="outlined"
                    size="small"
                  />
                  <Button 
                    size="small" 
                    onClick={resetToUniversalPrompt}
                    variant="outlined"
                  >
                    Reset
                  </Button>
                  <Button 
                    size="small" 
                    onClick={clearUniversalPrompt}
                    variant="outlined"
                    color="warning"
                  >
                    Clear
                  </Button>
                </Stack>
              )}
            </Box>
            
            <TextField
              value={systemPrompt}
              onChange={(e) => updateSystemPrompt(e.target.value)}
              variant="outlined"
              fullWidth
              multiline
              rows={4}
              placeholder="Enter system instructions for the model..."
              helperText={loadedUniversalPromptId ? 
                "You can edit this universal prompt locally. Use 'Reset' to restore the original." :
                "Enter your custom system prompt here, or load a universal prompt above."
              }
            />
            <Typography variant="body2" sx={{ textAlign: 'right', marginTop: 0.5, color: theme.palette.text.secondary }}>
              Token Estimate: {systemTokenCount}
            </Typography>
          </>
        ) : (
          <>
            {/* Non-admin users see read-only system prompt info */}
            {loadedUniversalPromptId && universalPrompts.length > 0 ? (
              <Box sx={{ mb: 2 }}>
                <Chip 
                  label={`Using Universal Prompt: ${universalPrompts.find(p => p.id === parseInt(loadedUniversalPromptId))?.title || 'Unknown'}`}
                  color="primary"
                  variant="outlined"
                  size="small"
                />
                <Typography variant="body2" sx={{ mt: 1, color: theme.palette.text.secondary }}>
                  Your system prompt is automatically managed by administrators.
                </Typography>
              </Box>
            ) : (
              <Typography variant="body2" sx={{ color: theme.palette.text.secondary, fontStyle: 'italic' }}>
                No universal prompt is currently configured. Contact an administrator.
              </Typography>
            )}
          </>
        )}
      </Box>

      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="body2" sx={{ marginBottom: 1, fontWeight: 'medium' }}>
          User Prompt:
        </Typography>
        <TextField
          value={userPrompt}
          onChange={(e) => updateUserPrompt(e.target.value)}
          variant="outlined"
          fullWidth
          multiline
          rows={4}
          placeholder="Enter input or question for the model..."
        />
        <Typography variant="body2" sx={{ textAlign: 'right', marginTop: 0.5, color: theme.palette.text.secondary }}>
          Token Estimate: {userTokenCount}
        </Typography>
      </Box>

      <Box sx={{ marginBottom: 0 }}>
        <Typography variant="body2" sx={{ marginBottom: 2, fontWeight: 'medium' }}>
          Number of Messages in Chat History to Include in Context: {historyLimit}
        </Typography>
        <Slider
          value={historyLimit}
          onChange={(e, value) => updateHistoryLimit(value)}
          valueLabelDisplay="auto"
          step={1}
          min={1}
          max={20}
          sx={{ marginBottom: 1 }}
        />
      </Box>
    </Paper>
  );
}
